import { FC, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { Box } from '@chakra-ui/react';
import { PaymentCardType } from '@shinetools/pricing-plan-library';
import { AnimatePresence, motion } from 'framer-motion';

import { PackageType } from '__generated__/GQL';
import asBentoPage from 'common/bento/hoc/asBentoPage';
import Button from 'components/_core/Button';
import Callout from 'components/_core/Callout';
import Heading from 'components/_core/Heading';
import SunshineCard from 'components/_core/SunshineCard';
import SuspenseQuery from 'components/SuspenseQuery';
import Header from 'features/Onboarding/components/Header';
import OBStickyButton from 'features/Onboarding/components/OBStickyButton/OBStickyButton';
import formatPrice from 'features/Onboarding/modules/PricingPlan/pages/PackageRecap/lib/formatPrice';

import { TopBar } from '../../components/StepsLayout';
import {
  OnboardingSasuOffersDocument,
  OnboardingSasuOffersQuery,
} from '../../graphql/queries/onboardingSasuOffers.gql';
import { OnboardingPackageItem } from '../../machine/model';
import basicCardSrc from './assets/basicCard.png';
import premiumCardSrc from './assets/premiumCard.png';
import { BasketItem } from './components/BasketItem';
import locales from './locales';

type SasuAddon =
  OnboardingSasuOffersQuery['viewer']['company']['onboardingSasuAddons'][0];

interface PackageRecapProps {
  addons: SasuAddon[];
  onNext: () => void;
  pack: OnboardingPackageItem;
  onPrev: () => void;
  referrer: string | null;
}

/**
 * A page where the user can see the outstanding agreement and accept it.
 */
const PackageRecap: FC<PackageRecapProps> = ({
  addons,
  onNext,
  onPrev,
  pack,
  referrer,
}) => {
  const cardImgSrc =
    pack.plan.paymentCard.type === PaymentCardType.BasicDebitCard
      ? basicCardSrc
      : premiumCardSrc;

  const {
    'capital-deposit': capitalDepositAddon,
    'sasu-administrative-fees': administrativeFeesAddon,
    'sasu-formalities': sasuFormalitiesAddon,
  } = useMemo(
    () =>
      addons.reduce(
        (obj, { providerAddonId, ...rest }) => ({
          ...obj,
          [providerAddonId]: rest,
        }),
        {} as {
          'capital-deposit'?: Omit<SasuAddon, 'providerAddonId'>;
          'sasu-administrative-fees'?: Omit<SasuAddon, 'providerAddonId'>;
          'sasu-formalities'?: Omit<SasuAddon, 'providerAddonId'>;
        },
      ),
    [addons],
  );

  if (
    !capitalDepositAddon ||
    !administrativeFeesAddon ||
    !sasuFormalitiesAddon
  ) {
    throw new Error('Missing addon information');
  }

  const subscriptionYearPackagePrice =
    pack.discountPriceWithoutVAT - capitalDepositAddon.basePriceWithoutVAT;

  const totalAmountWithoutVAT =
    pack.discountPriceWithoutVAT +
    sasuFormalitiesAddon.basePriceWithoutVAT +
    administrativeFeesAddon.basePriceWithoutVAT;
  const totalAmountWithVAT =
    pack.discountPrice +
    sasuFormalitiesAddon.basePrice +
    administrativeFeesAddon.basePrice;
  const subscriptionYearOutOfPackagePrice = pack.plan.priceWithoutVAT * 12;

  return (
    <Box maxWidth="560px" paddingTop="space-32">
      <Header
        display={{
          base: 'none',
          md: 'block',
        }}
        title={locales.title}
      />
      <TopBar.Title>{locales.title}</TopBar.Title>

      <SunshineCard
        border={{
          base: 'none',
          lg: '1px solid',
        }}
        borderColor={{
          base: 'none',
          lg: 'grey.300',
        }}
        padding={0}
      >
        <Box padding="space-24">
          <Heading marginBottom="space-24" size="sm" variant="number">
            {locales.creationPackage}
          </Heading>
          <BasketItem
            label="Documents et formalités"
            price={sasuFormalitiesAddon.basePriceWithoutVAT}
            showExcludingVat
          />
          <BasketItem
            label="Frais administratifs"
            price={administrativeFeesAddon.basePriceWithoutVAT}
            showExcludingVat
          />
          <BasketItem
            label="Dépôt de capital"
            price={capitalDepositAddon.basePriceWithoutVAT}
            showExcludingVat
          />
        </Box>
        <Box
          borderTop={{
            base: '6px solid',
            lg: '1px solid',
          }}
          borderTopColor={{
            base: 'grey.300',
            lg: 'grey.300',
          }}
          padding="space-24"
        >
          <Heading marginBottom="space-24" size="sm" variant="number">
            {locales.proAccount}
          </Heading>
          <BasketItem
            imgSrc={cardImgSrc}
            initialPrice={subscriptionYearOutOfPackagePrice}
            label={locales.formatString(locales.includedSubscription, {
              brandName: pack.plan.brandName,
              trialPeriod: 12,
            })}
            offer={
              referrer
                ? locales.formatString(locales.referralOffer, referrer)
                : undefined
            }
            price={subscriptionYearPackagePrice}
            showExcludingVat
          />
          <Button
            marginTop="space-24"
            onClick={onPrev}
            variant="inline-secondary"
          >
            {locales.changePlan}
          </Button>
        </Box>
        <Box
          borderTop={{
            base: '6px solid',
            lg: '1px solid',
          }}
          borderTopColor={{
            base: 'grey.300',
            lg: 'grey.300',
          }}
          padding="space-24"
        >
          <BasketItem
            boldLabel
            label={`${locales.total} ${locales.vat.excluded}`}
            price={totalAmountWithoutVAT}
          />
          <BasketItem
            boldLabel
            label={locales.tax}
            price={totalAmountWithVAT - totalAmountWithoutVAT}
          />
        </Box>
        <Box
          borderTop={{
            base: '6px solid',
            lg: '1px solid',
          }}
          borderTopColor={{
            base: 'grey.300',
            lg: 'grey.300',
          }}
          padding="space-24"
          paddingBottom={{
            base: 0,
            lg: 'space-24',
          }}
        >
          <BasketItem
            boldLabel
            label={`${locales.total} ${locales.vat.included}`}
            price={totalAmountWithVAT}
          />
          <Callout hideIcon>
            <Callout.Title>{locales.calloutTitle}</Callout.Title>
            <Callout.Content>{locales.calloutDescription}</Callout.Content>
          </Callout>
        </Box>
      </SunshineCard>

      <OBStickyButton icon="arrow-right" onClick={onNext}>
        {locales.pay} {formatPrice(totalAmountWithVAT)} {locales.vat.included}
      </OBStickyButton>
    </Box>
  );
};

interface PackageRecapContainerProps {
  onNext: () => void;
  providerPackageId: string;
  companyProfileId: string;
  onPrev: () => void;
  referrer: string | null;
}

const PackageRecapContainer = asBentoPage<PackageRecapContainerProps>(
  ({ companyProfileId, onPrev, providerPackageId, ...props }) => {
    const query = useQuery(OnboardingSasuOffersDocument, {
      variables: {
        companyProfileId,
        input: {
          type: PackageType.CapitalDeposit,
        },
      },
    });

    return (
      <Box
        maxWidth="972px"
        paddingBottom={{
          base: 0,
          lg: 'space-80',
        }}
        paddingLeft="space-16"
        paddingRight="space-16"
        width="full"
      >
        <AnimatePresence>
          <motion.div exit={{ opacity: 0, y: 10 }}>
            <SuspenseQuery query={query}>
              {({ viewer: { company } }) => {
                const pack = company.onboardingPackages.find(
                  (p) => p.providerPackageId === providerPackageId,
                );
                const { onboardingSasuAddons } = company;

                if (!pack) {
                  throw new Error('Package not found');
                }

                return (
                  <motion.div
                    animate={{ opacity: 1, y: 0 }}
                    initial={{ opacity: 0, y: 20 }}
                    transition={{ delay: 0.3 }}
                  >
                    <PackageRecap
                      {...props}
                      addons={onboardingSasuAddons}
                      onPrev={onPrev}
                      pack={pack as OnboardingPackageItem} // Casting `providerPackageId` to union type
                    />
                  </motion.div>
                );
              }}
            </SuspenseQuery>
          </motion.div>
        </AnimatePresence>
      </Box>
    );
  },
);

export default PackageRecapContainer;
